// frontend/web-ui/src/components/lists/picker/usePickerInfiniteQuery.ts
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  searchQuery,
  SEARCH_QUERY_KEY,
} from "../../../screens/search/api/searchV2Api";
import type {
  SearchQueryResponse,
  SearchSeriesItem,
  SearchReleaseItem,
} from "../../../screens/search/types";
import type { ServerResponse } from "../../../types";
import { mapSeriesToPickerItem, mapReleaseToPickerItem } from "./adapters";
import type { PickerItem, PickerListType } from "./types";

const PERPAGE = 24; // matches browse (frontend/web-ui/src/screens/browse/hooks/useSearchV2API.ts)

interface UsePickerInfiniteQueryArgs {
  listType: PickerListType;
  /** Trimmed search query. Empty string is the default-feed mode. */
  query: string;
  /** Picker visibility — when false, no fetch fires. */
  enabled: boolean;
}

export function usePickerInfiniteQuery({
  listType,
  query,
  enabled,
}: UsePickerInfiniteQueryArgs) {
  const context = listType === "releases" ? "releases" : "series";

  const result = useInfiniteQuery({
    queryKey: [...SEARCH_QUERY_KEY, "list-picker", context, query],
    queryFn: async ({ pageParam = 1, signal }) => {
      const res = await searchQuery(
        {
          q: query,
          context,
          p: pageParam,
          perpage: PERPAGE,
          enrich: false,
          semantic: true,
        },
        signal,
      );
      return (res.data as unknown as ServerResponse<SearchQueryResponse>).payload
        .results;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const set = listType === "releases" ? lastPage.releases : lastPage.series;
      const total = set?.total ?? 0;
      const totalPages = Math.ceil(total / PERPAGE);
      return lastPageParam < totalPages ? lastPageParam + 1 : undefined;
    },
    enabled,
    staleTime: 60_000,
  });

  // Flatten pages → PickerItem[]
  const items: PickerItem[] = (result.data?.pages ?? []).flatMap((page) => {
    if (listType === "releases") {
      return (page.releases?.items ?? []).map((it: SearchReleaseItem) =>
        mapReleaseToPickerItem(it),
      );
    }
    return (page.series?.items ?? []).map((it: SearchSeriesItem) =>
      mapSeriesToPickerItem(it),
    );
  });

  return {
    items,
    isLoading: result.isLoading,
    isFetchingNextPage: result.isFetchingNextPage,
    fetchNextPage: result.fetchNextPage,
    hasNextPage: !!result.hasNextPage,
    isError: result.isError,
  };
}
